// A LINKED nav section takes its manufacturing line and its solution from another section instead of choosing
// its own. The pairing is decided server-side and persisted -- a corrugated section points at the
// carton_lamination section that leads it (ManufacturingCostCalculatorNavBuilder
// #link_corrugated_offset_to_carton_lamination), stored as
// user_quote_nav_sections.solution_user_quote_nav_section_id.
//
// Frank READS that link; it never re-derives the pairing from part types. Two reasons. The rule belongs to one
// place, so the /mcc page and the legacy Manufacturing Cost Calculator cannot come to different conclusions
// about the same quote. And frank.contract allows part_type to branch in exactly two places, neither of them
// here.
//
// The records come from window.user_quote_nav_sections -- the same source the legacy listing reads
// (LayoutNavSectionCombinationList's isLinkedSection), refreshed by every select-solution response. Sections are
// addressed by `sort_order`, which is the index every /mcc endpoint and the address bar key off; the link itself
// is by `id`, which is why resolving a parent needs both.

// The nav section record at this index, or null.
const sectionAt = (userQuoteNavSections, sectionIndex) => (
  (userQuoteNavSections || []).find((section) => section.sort_order === sectionIndex) || null
);

// The section this one inherits from, or null when it chooses for itself.
//
// A link whose parent is missing from the list returns null -- the section then behaves as an ordinary one
// rather than rendering as "inherited from" nothing. That is a stale window payload, not a state to invent a
// parent for.
export const linkedParentOf = (userQuoteNavSections, sectionIndex) => {
  const section = sectionAt(userQuoteNavSections, sectionIndex);
  if (!section || !section.solution_user_quote_nav_section_id) { return null; }

  return (userQuoteNavSections || []).find(
    (candidate) => candidate.id === section.solution_user_quote_nav_section_id
  ) || null;
};

// True when this section inherits rather than chooses. Kept separate from linkedParentOf because the callers
// that only gate behaviour should not have to care whether the parent resolved.
export const isLinkedSection = (userQuoteNavSections, sectionIndex) => {
  const section = sectionAt(userQuoteNavSections, sectionIndex);
  return Boolean(section && section.solution_user_quote_nav_section_id);
};

// The sort_orders of every section that inherits -- what the setup wizard skips, since a section with nothing to
// choose has nothing to ask about.
export const linkedSectionIndexes = (userQuoteNavSections) => (
  (userQuoteNavSections || [])
    .filter((section) => section.solution_user_quote_nav_section_id)
    .map((section) => section.sort_order)
);
